// ============================================================
// Project store — the active project + the list the user can switch to.
// Sits alongside the grid store; selecting a project hot-reloads the engine
// (via lib/projects.switchProject) and bumps grid so every view re-renders.
// ============================================================
import { create } from "zustand";
import {
  listMyProjects,
  switchProject,
  getProjectId,
  type ProjectSummary,
} from "@/lib/projects";
import { useGrid } from "./grid";

interface ProjectState {
  projects: ProjectSummary[];
  currentId: string;
  loading: boolean;
  load: () => Promise<void>;
  select: (id: string) => Promise<void>;
  current: () => ProjectSummary | undefined;
}

export const useProjects = create<ProjectState>((set, get) => ({
  projects: [],
  currentId: getProjectId(),
  loading: false,
  load: async () => {
    set({ loading: true });
    const projects = await listMyProjects();
    // If the persisted current id isn't in the accessible set, fall back to the
    // first one the user can actually open.
    const currentId = projects.some((p) => p.id === get().currentId)
      ? get().currentId
      : projects[0]?.id || get().currentId;
    set({ projects, currentId, loading: false });
  },
  select: async (id) => {
    if (id === get().currentId) return;
    set({ loading: true });
    await switchProject(id);
    set({ currentId: id, loading: false });
    useGrid.getState().bump();
  },
  current: () => get().projects.find((p) => p.id === get().currentId),
}));
